import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.order) setOrder(data.order);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
        // keep trying
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <>
      <SEO title="Payment Status" description="Your booking payment status." path="/payment/success" />
      <section className="flex min-h-[70vh] items-center justify-center px-5 py-32">
        <div className="w-full max-w-lg rounded-3xl card-3d p-10 text-center" data-testid="payment-success-page">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto h-14 w-14 animate-spin text-primary" />
              <h1 className="mt-6 font-display text-2xl font-bold text-navy">Confirming your payment…</h1>
              <p className="mt-2 text-slate-500">Hang tight — this only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-16 w-16 text-secondary" data-testid="payment-paid-icon" />
              <h1 className="mt-6 font-display text-3xl font-bold text-navy">Booking confirmed! 🎉</h1>
              <p className="mt-3 text-slate-600">
                Thank you — your payment went through. A confirmation email is on its way, and a
                Navigator specialist will reach out within 24 hours to finalize your itinerary.
              </p>
              {order && (
                <div className="mt-6 rounded-2xl bg-muted p-5 text-left text-sm" data-testid="success-order-summary">
                  <SummaryRow label="Package" value={order.package_name} />
                  {order.room_name && <SummaryRow label="Room" value={order.room_name} />}
                  <SummaryRow label="Stay" value={`${order.date_label || "Flexible"} · ${order.nights || 1} night(s) · ${order.rooms || 1} room(s)`} />
                  <SummaryRow label="Travelers" value={order.travelers} />
                  <SummaryRow label="Amount paid" value={`$${Number(order.amount || 0).toLocaleString()}`} />
                  {order.balance_due > 0 && (
                    <SummaryRow label="Balance due later" value={`$${Number(order.balance_due).toLocaleString()}`} />
                  )}
                </div>
              )}
              <Link
                to="/packages"
                data-testid="success-back-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground btn-glow hover:scale-105"
              >
                Browse more escapes <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
          {state === "timeout" && (
            <>
              <Loader2 className="mx-auto h-14 w-14 text-slate-300" />
              <h1 className="mt-6 font-display text-2xl font-bold text-navy">Still processing…</h1>
              <p className="mt-2 text-slate-500">
                Your payment is taking a little longer than usual. If it was successful you'll
                receive a confirmation email shortly. You can safely close this page.
              </p>
              <Link to="/packages" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                Back to packages
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="mx-auto h-16 w-16 text-primary" />
              <h1 className="mt-6 font-display text-2xl font-bold text-navy">Something went wrong</h1>
              <p className="mt-2 text-slate-500">
                We couldn't confirm this payment. If you were charged, contact us and we'll sort it
                out right away.
              </p>
              <Link to="/packages" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                Back to packages
              </Link>
            </>
          )}
        </div>
      </section>
    </>
  );
}

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-200/60 py-1.5 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-navy">{value}</span>
  </div>
);
